import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Container, Paper, TextField, Typography } from '@mui/material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

/**
 * /setup?token=… — the one-time link from a partner owner's welcome email.
 * Shows who the link is for, sets their password, then sends them to log in.
 * Without a token (a logged-in owner reopening setup) it sends them on to the
 * wizard once that exists; for now that is the dashboard.
 */
export default function ClaimPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [claim, setClaim] = useState(null); // { orgName, slug, firstName, username }
  const [checking, setChecking] = useState(!!token);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate(localStorage.getItem('token') ? '/setup/wizard' : '/login', { replace: true });
      return;
    }
    let alive = true;
    axios.get(`${API}/api/org/setup/claim`, { params: { token } })
      .then(({ data }) => { if (alive) setClaim(data); })
      .catch((err) => { if (alive) setError(err.response?.data?.message || 'This setup link is invalid or has expired.'); })
      .finally(() => { if (alive) setChecking(false); });
    return () => { alive = false; };
  }, [token, navigate]);

  const strong = password.length >= 8 && /[A-Za-z]/.test(password) && /[0-9]/.test(password);

  const submit = async (e) => {
    e.preventDefault();
    if (!strong) { setError('Choose a password of at least 8 characters with letters and numbers.'); return; }
    if (password !== confirm) { setError('The two passwords do not match.'); return; }
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API}/api/org/setup/claim`, { token, password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true, state: { username: claim?.username, next: '/setup/wizard' } }), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not set your password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4 }}>
        {checking ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}><CircularProgress size={24} /><Typography>Checking your link…</Typography></Box>
        ) : error && !claim ? (
          <>
            <Typography variant="h5" gutterBottom>This link cannot be used</Typography>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Typography variant="body2" color="text.secondary">Ask the person who set up your company to send a new setup link.</Typography>
          </>
        ) : done ? (
          <>
            <Typography variant="h5" gutterBottom>Password set</Typography>
            <Alert severity="success">Taking you to the sign-in page. Your username is <b>{claim?.username}</b>.</Alert>
          </>
        ) : (
          <form onSubmit={submit}>
            <Typography variant="h5" gutterBottom>Welcome, {claim?.firstName}</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Set the password for your <b>{claim?.orgName}</b> owner account. Your username is <b>{claim?.username}</b>.
            </Typography>
            <TextField fullWidth type="password" label="New password" margin="normal" value={password} onChange={(e) => setPassword(e.target.value)}
              helperText="At least 8 characters, with letters and numbers" autoFocus autoComplete="new-password" />
            <TextField fullWidth type="password" label="Confirm password" margin="normal" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            <Button fullWidth type="submit" variant="contained" sx={{ mt: 3 }} disabled={saving || !password || !confirm}>
              {saving ? 'Saving…' : 'Set password and continue'}
            </Button>
          </form>
        )}
      </Paper>
    </Container>
  );
}
